import React, { memo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from "recharts";

const BAR_COLOR = "#2ec4b6";

const HorizontalBarChartSlider = ({
  title = "Students per District & ITI",
  loading = false,
  districtData = [],
  itiData = [],
}) => {
  const [carouselIndex, setCarouselIndex] = React.useState(0);
  const [activeBar, setActiveBar] = React.useState(null);

  const districts = React.useMemo(
    () => districtData.map((d) => d.name),
    [districtData],
  );

  const currentDistrict = districts[carouselIndex];

  const totalStudents = React.useMemo(() => {
    return itiData
      .filter((i) => i.district === currentDistrict)
      .reduce((sum, i) => sum + (i.value || 0), 0);
  }, [itiData, currentDistrict]);

  const chartData = React.useMemo(() => {
    return itiData
      .filter((i) => i.district === currentDistrict)
      .map((i) => ({
        iti: i.name,
        [currentDistrict]: i.value || 0,
      }));
  }, [itiData, currentDistrict]);

  const handlePrev = () =>
    setCarouselIndex((i) => (i === 0 ? districts.length - 1 : i - 1));

  const handleNext = () =>
    setCarouselIndex((i) => (i === districts.length - 1 ? 0 : i + 1));

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      {/* Title */}
      <p className="font-bold text-lg mb-4">{title}</p>
      <hr className="dashboard-divider mb-4" />

      {/* Loading */}
      {loading ? (
        <div className="w-full h-[60%] flex items-center justify-center bg-gray-50 border rounded-lg p-10 animate-pulse">
          <span className="text-gray-500 font-medium">Loading Chart...</span>
        </div>
      ) : !districtData.length || !itiData.length ? (
        <div className="flex items-center justify-center h-60 text-gray-500">
          No Data Available
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <button onClick={handlePrev} className="text-primary focus:outline-none">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>

            <div className="text-center">
              <div className="font-semibold">{currentDistrict}</div>
              <div className="text-sm text-gray-600">
                Total Students:{" "}
                <span className="font-semibold text-primary">
                  {totalStudents}
                </span>
              </div>
            </div>

            <button onClick={handleNext} className="text-primary focus:outline-none">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          </div>

          {/* Chart */}
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 5, right: 20, left: 60, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="iti" type="category" width={120} />

              <Tooltip
                cursor={{ fill: "transparent" }}
                content={({ active, payload }) =>
                  active && payload?.length ? (
                    <div className="bg-gray-900 text-white px-3 py-2 rounded text-sm">
                      <div>{payload[0].value} Students</div>
                    </div>
                  ) : null
                }
              />

              <Bar
                dataKey={currentDistrict}
                barSize={22}
                radius={[0, 6, 6, 0]}
                fill={BAR_COLOR}
              >
                {chartData.map((_, i) => (
                  <Cell
                    key={i}
                    fillOpacity={
                      activeBar === null || activeBar === i ? 1 : 0.4
                    }
                    onMouseEnter={() => setActiveBar(i)}
                    onMouseLeave={() => setActiveBar(null)}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-3">
            {districts.map((_, i) => (
              <span
                key={i}
                onClick={() => setCarouselIndex(i)}
                className={`h-2 w-2 rounded-full cursor-pointer ${
                  i === carouselIndex ? "bg-primary" : "bg-gray-300"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default memo(HorizontalBarChartSlider);
